import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTheme } from "./shared";

const STATEMENTS = [
    "DON'T DECORATE THE SPACE.",
    "TRANSFORM IT.",
    "MAKE PEOPLE STOP.",
    "MAKE THEM LOOK AGAIN.",
    "MAKE THEM REMEMBER.",
];

const Sculpture = ({ progress }) => {
    const { theme } = useTheme();
    const day = theme === "day";
    const rotate = useTransform(progress, [0, 1], [0, 240]);
    const spread = useTransform(progress, [0, 0.5, 1], [0.55, 1.1, 0.7]);
    const glow = useTransform(progress, [0, 0.5, 1], [0.25, 0.75, 0.35]);
    const rodBg = day
        ? "linear-gradient(to right, rgba(148,98,28,0.85), transparent)"
        : "linear-gradient(to right, rgba(255,232,182,0.9), transparent)";
    const rodGlow = day ? "0 0 14px 1px rgba(190,140,50,0.35)" : "0 0 14px 1px rgba(255,214,140,0.4)";
    const dotStyle = day
        ? { background: "#8A5A12", boxShadow: "0 0 30px 10px rgba(190,140,50,0.3)" }
        : { background: "#FFE8B6", boxShadow: "0 0 30px 10px rgba(255,214,140,0.35)" };
    return (
        <motion.div
            style={{ rotate, scale: spread, opacity: glow }}
            className="pointer-events-none absolute left-1/2 top-1/2 h-[46vmin] w-[46vmin] -translate-x-1/2 -translate-y-1/2"
        >
            {Array.from({ length: 14 }).map((_, i) => (
                <span
                    key={i}
                    className="absolute left-1/2 top-1/2 h-px w-1/2 origin-left"
                    style={{
                        transform: `rotate(${i * (360 / 14)}deg)`,
                        background: rodBg,
                        boxShadow: rodGlow,
                        transition: "box-shadow 900ms",
                    }}
                />
            ))}
            <span
                className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ ...dotStyle, transition: "background-color 900ms, box-shadow 900ms" }}
            />
        </motion.div>
    );
};

export const Wow = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const seg = 1 / STATEMENTS.length;

    return (
        <section ref={ref} data-testid="wow-section" className="relative bg-black" style={{ height: `${STATEMENTS.length * 120}vh` }}>
            <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
                <Sculpture progress={scrollYProgress} />
                {STATEMENTS.map((s, i) => {
                    const start = i * seg;
                    const isLast = i === STATEMENTS.length - 1;
                    return (
                        <WowLine key={i} progress={scrollYProgress} range={[start + 0.02, start + seg * 0.35, start + seg * 0.7, isLast ? 1 : start + seg * 0.95]} isLast={isLast}>
                            {s}
                        </WowLine>
                    );
                })}
            </div>
        </section>
    );
};

const WowLine = ({ progress, range, isLast, children }) => {
    const opacity = useTransform(progress, range, [0, 1, 1, isLast ? 1 : 0]);
    const y = useTransform(progress, range, [60, 0, 0, isLast ? 0 : -60]);
    return (
        <motion.h2
            style={{ opacity, y }}
            className="absolute inset-x-0 px-6 text-center font-display text-[9vw] font-bold uppercase leading-[0.95] tracking-tighter text-[#F4F2EF] text-glow-warm sm:text-7xl lg:text-8xl"
        >
            {children}
        </motion.h2>
    );
};
